import React, { useContext, useRef, useState } from "react";
import DatePicker from "../../components/DatePicker";
import CustomInput from "../../components/CustomInput";
import CustomSelect from "../../components/CustomSelect";
import { toast } from "react-toastify";
import { convertDateFormat } from "../../../../utils/DateFormat";
import { managerService } from "../../../../services/api/managerService";
import { AuthContext } from "../../../../contexts/AuthContext";
import Loading from "../../components/Loading";
import { useNavigate } from "react-router-dom";
import ConfirmDeleteModal from "../../components/DeleteConfirmModal";
import MobilePatternHeader from "../../components/MobilePatternHeader";
import Button from "../../components/Button";

/* ================= Helpers & constants ================= */

const GENDER_OPTIONS = [
  { value: "Nam", label: "Nam" },
  { value: "Nữ", label: "Nữ" },
  { value: "Khác", label: "Khác" },
];

const ROLE_OPTIONS = [
  { value: "Staff", label: "Nhân viên" },
  { value: "Collaborator", label: "Cộng tác viên" },
];

const emailIsValid = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
const phoneIsValidVN = (phone) =>
  /^(0(3|5|7|8|9)\d{8}|\+84(3|5|7|8|9)\d{8})$/.test(String(phone || "").trim());

const buildPayload = (data, imageFile) => {
  const base = {
    fullName: data.fullName,
    gender: data.gender?.value || "",
    dateOfBirth: data.birth ? convertDateFormat(data.birth) : "",
    email: data.email,
    phone: data.phone,
    address: {
      city: data.city,
      district: data.district,
      detail: data.address,
    },
    staffCode: data.staffCode,
    username: data.username,
    joinDate: data.onBoardDate ? convertDateFormat(data.onBoardDate) : "",
    role: data.role?.value || "",
    password: data.password,
  };

  if (imageFile) {
    const fd = new FormData();
    Object.entries(base).forEach(([k, v]) => {
      if (k === "address") {
        fd.append("address[city]", v.city || "");
        fd.append("address[district]", v.district || "");
        fd.append("address[detail]", v.detail || "");
      } else {
        fd.append(k, v ?? "");
      }
    });
    fd.append("avatar", imageFile);
    return fd;
  }
  return base;
};

/* ================= Component ================= */
const EmployeeForm = ({ isMobile }) => {
  const navigate = useNavigate();
  const { token_client } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const [imageSrc, setImageSrc] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const objectUrlRef = useRef(null);

  const [error, setError] = useState({});
  const [data, setData] = useState({
    fullName: "",
    birth: "",
    gender: null,
    email: "",
    onBoardDate: "",
    phone: "",
    city: "",
    district: "",
    address: "",
    staffCode: "",
    username: "",
    password: "",
    role: null,
  });

  const [openPicker, setOpenPicker] = useState(null);
  const handleDatePickerToggle = (picker) => {
    setOpenPicker((prev) => (prev === picker ? null : picker));
  };

  const handleSelectChange = (name, selected) => {
    setData((prev) => ({ ...prev, [name]: selected }));
    setError((prev) => ({ ...prev, [name]: undefined }));
  };
  const handleChange = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
    setError((prev) => ({ ...prev, [field]: undefined }));
  };

  /* ---------- Image (preview + cleanup) ---------- */
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.dismiss();
      toast.error("Chỉ chấp nhận định dạng JPEG/PNG.");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.dismiss();
      toast.error("Kích thước tệp vượt quá 25MB.");
      return;
    }

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    setImageSrc(url);
    setImageFile(file);
  };

  React.useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  /* ---------- Validate ---------- */
  const validateFields = () => {
    const errs = {};
    const required = [
      "fullName",
      "email",
      "phone",
      "gender",
      "role",
      "staffCode",
      "username",
      "password",
    ];
    required.forEach((k) => {
      const v = data[k];
      if (v == null || (typeof v === "string" && !v.trim())) {
        errs[k] = "Trường này không được để trống.";
      }
    });

    if (data.email && !emailIsValid(data.email))
      errs.email = "Email không hợp lệ.";
    if (!phoneIsValidVN(data.phone)) errs.phone = "Số điện thoại không hợp lệ.";
    if (data.password && data.password.length < 6)
      errs.password = "Mật khẩu phải lớn hơn 6 kí tự";

    setError(errs);
    return Object.keys(errs).length === 0;
  };

  /* ---------- Submit ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!validateFields()) return;

    try {
      setIsSubmitting(true);
      setLoading(true);

      const payload = buildPayload(data, imageFile);
      await managerService.create_user(payload, token_client);

      toast.success("Tạo nhân viên thành công!");
      navigate("/tai-khoan-nhan-vien");
    } catch (error) {
      console.error(error);
      toast.dismiss();
      toast.error(
        error?.response?.data?.message ||
          "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  /* ---------- Cancel Handler ---------- */
  const handleCancelConfirm = () => {
    setShowCancelModal(false);
    navigate("/tai-khoan-nhan-vien");
  };

  /* ---------- Render ---------- */
  if (loading && !isSubmitting) {
    return (
      <div
        className={`flex ${
          isMobile ? "h-screen w-screen" : "h-full"
        } items-center justify-center`}
      >
        <Loading />
      </div>
    );
  }

  /* ===================== MOBILE ===================== */
  if (isMobile) {
    return (
      <div className="h-full w-full">
        <MobilePatternHeader
          title="Thêm mới nhân viên"
          onBack={() => navigate(-1)}
        />

        <div className="h-[calc(100%-64px)] bg-[#F2F2F2] p-3">
          <form
            className="h-[calc(100%-70px)] w-full overflow-auto"
            onSubmit={handleSubmit}
          >
            {/* Image Upload */}
            <div className="w-full flex flex-col items-center justify-center mt-1">
              <label
                htmlFor="image-upload"
                className="cursor-pointer size-[100px] rounded-full bg-white flex items-center justify-center overflow-hidden relative hover:opacity-60"
              >
                {imageSrc ? (
                  <img
                    src={imageSrc}
                    alt="Uploaded"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <img src="/images/input-default.png" alt="Default icon" />
                )}
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </label>
              <p className="text-[14px] font-sf-medium text-[#0267FF] mt-1">
                Tải ảnh lên
              </p>
            </div>

            {/* General */}
            <div className="space-y-3">
              <p className="text-[14px] font-sf-medium text-[#0267FF] pb-1 border-b border-[#E1E5F3]">
                Thông tin chung
              </p>

              <CustomInput
                placeholder="Nhập họ tên"
                required
                value={data.fullName}
                onChange={(v) => handleChange("fullName", v)}
                error={error.fullName}
              />

              <div className="grid grid-cols-2 gap-3">
                <DatePicker
                  placeholder="Ngày sinh"
                  onChange={(v) => handleChange("birth", v)}
                  height="52px"
                  isCalendarOpen={openPicker === "birthDate"}
                  onToggle={() => handleDatePickerToggle("birthDate")}
                />
                <CustomSelect
                  height="52px"
                  required
                  options={GENDER_OPTIONS}
                  placeholder="Giới tính"
                  value={data.gender}
                  onChange={(opt) => handleSelectChange("gender", opt)}
                  className="bg-white"
                  error={error.gender}
                />
                <DatePicker
                  placeholder="Ngày vào làm"
                  onChange={(v) => handleChange("onBoardDate", v)}
                  height="52px"
                  isCalendarOpen={openPicker === "onBoardDate"}
                  onToggle={() => handleDatePickerToggle("onBoardDate")}
                />
              </div>

              <CustomInput
                placeholder="Email"
                type="email"
                value={data.email}
                required
                error={error.email}
                onChange={(v) => handleChange("email", v)}
              />

              <div className="grid grid-cols-2 gap-3">
                <CustomInput
                  placeholder="Tỉnh/Thành phố"
                  value={data.city}
                  error={error.city}
                  onChange={(v) => handleChange("city", v)}
                />
                <CustomInput
                  placeholder="Quận/Huyện"
                  value={data.district}
                  error={error.district}
                  onChange={(v) => handleChange("district", v)}
                />
              </div>
              <CustomInput
                placeholder="Địa chỉ"
                value={data.address}
                error={error.address}
                onChange={(v) => handleChange("address", v)}
              />
            </div>

            {/* Account */}
            <div className="space-y-3 mt-3 pb-10vh">
              <p className="text-[14px] font-sf-medium text-[#0267FF] pb-1 border-b border-[#E1E5F3]">
                Thông tin tài khoản
              </p>

              <CustomInput
                placeholder="Tên tài khoản"
                required
                value={data.username}
                error={error.username}
                onChange={(v) => handleChange("username", v)}
              />

              <div className="grid grid-cols-2 gap-3">
                <CustomInput
                  placeholder="Mã số nhân viên"
                  required
                  value={data.staffCode}
                  error={error.staffCode}
                  onChange={(v) => handleChange("staffCode", v)}
                />
                <CustomInput
                  placeholder="Mật khẩu"
                  type="password"
                  required
                  value={data.password}
                  error={error.password}
                  onChange={(v) => handleChange("password", v)}
                />
              </div>

              <CustomSelect
                height="52px"
                required
                options={ROLE_OPTIONS}
                placeholder="Phân quyền"
                value={data.role}
                error={error.role}
                onChange={(opt) => handleSelectChange("role", opt)}
                className="bg-white"
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-end space-x-4 my-3 w-full">
              <Button
                type="submit"
                disabled={isSubmitting}
                isLoading={isSubmitting}
                variant="primary"
                size="lg"
                className="w-full"
              >
                {isSubmitting ? "Đang tạo..." : "Tạo mới"}
              </Button>
            </div>
          </form>
        </div>

        {/* Cancel Confirmation Modal */}
        <ConfirmDeleteModal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          onConfirm={handleCancelConfirm}
          title="Xác nhận hủy"
          message="Bạn có chắc chắn muốn hủy thao tác này không?"
        />
      </div>
    );
  }

  /* ===================== DESKTOP ===================== */
  return (
    <div className="h-full overflow-auto">
      <div className="bg-white p-6 px-[5vw] lap:px-[10vw] 2xl:px-[15vw] rounded-lg">
        <div className="flex items-center gap-2 mb-6">
          <img src="/icons/user.svg" alt="User Icons" />
          <h2>Thông tin chung</h2>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Image Upload */}
          <div className="flex items-center">
            <label
              htmlFor="image-upload"
              className="cursor-pointer w-28 h-28 bg-gray-100 flex items-center justify-center rounded-md overflow-hidden relative hover:opacity-60"
            >
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt="Uploaded"
                  className="w-full h-full object-cover"
                />
              ) : (
                <img src="/icons/camera-icon.svg" alt="Camera icon" />
              )}
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4 my-4">
            {/* Row 1: Họ tên + Ngày sinh */}
            <CustomInput
              placeholder="Nhập họ tên"
              required
              value={data.fullName}
              onChange={(v) => handleChange("fullName", v)}
              error={error.fullName}
            />
            <DatePicker
              placeholder="Ngày sinh"
              onChange={(v) => handleChange("birth", v)}
              height="52px"
              isCalendarOpen={openPicker === "birthDate"}
              onToggle={() => handleDatePickerToggle("birthDate")}
            />

            {/* Row 2: Email + Ngày vào làm */}
            <CustomInput
              placeholder="Email"
              type="email"
              required
              value={data.email}
              error={error.email}
              onChange={(v) => handleChange("email", v)}
            />
            <DatePicker
              placeholder="Ngày vào làm"
              onChange={(v) => handleChange("onBoardDate", v)}
              height="52px"
              isCalendarOpen={openPicker === "onBoardDate"}
              onToggle={() => handleDatePickerToggle("onBoardDate")}
            />

            {/* Row 3: Giới tính + Số điện thoại */}
            <CustomSelect
              height="52px"
              required
              options={GENDER_OPTIONS}
              placeholder="Giới tính"
              value={data.gender}
              onChange={(opt) => handleSelectChange("gender", opt)}
              className="bg-white"
              error={error.gender}
            />
            <CustomInput
              placeholder="Số điện thoại"
              required
              value={data.phone}
              error={error.phone}
              onChange={(v) => handleChange("phone", v)}
            />

            {/* Row 4: Tỉnh/TP + Quận/Huyện */}
            <CustomInput
              placeholder="Tỉnh/Thành phố"
              value={data.city}
              error={error.city}
              onChange={(v) => handleChange("city", v)}
            />
            <CustomInput
              placeholder="Quận/Huyện"
              value={data.district}
              error={error.district}
              onChange={(v) => handleChange("district", v)}
            />

            {/* Row 5: Địa chỉ (full width) */}
            <div className="col-span-2">
              <CustomInput
                placeholder="Địa chỉ"
                value={data.address}
                error={error.address}
                onChange={(v) => handleChange("address", v)}
              />
            </div>
          </div>

          {/* Account */}
          <div className="flex items-center gap-2 my-6">
            <img src="/icons/bag.svg" alt="User Icons" />
            <h2>Thông tin tài khoản</h2>
          </div>

          <div className="grid grid-cols-3 gap-4 my-4">
            <CustomInput
              placeholder="Mã số nhân viên"
              required
              value={data.staffCode}
              error={error.staffCode}
              onChange={(v) => handleChange("staffCode", v)}
            />
            <CustomInput
              placeholder="Tên tài khoản"
              required
              value={data.username}
              error={error.username}
              onChange={(v) => handleChange("username", v)}
            />
            <CustomInput
              placeholder="Mật khẩu"
              type="password"
              required
              value={data.password}
              error={error.password}
              onChange={(v) => handleChange("password", v)}
            />
          </div>

          <CustomSelect
            height="52px"
            required
            options={ROLE_OPTIONS}
            placeholder="Phân quyền"
            value={data.role}
            error={error.error}
            onChange={(opt) => handleSelectChange("role", opt)}
            className="bg-white"
          />

          {/* Buttons */}
          <div className="flex justify-end space-x-4 mt-6">
            <Button
              type="button"
              variant="secondary"
              size="lg"
              onClick={() => setShowCancelModal(true)}
            >
              Hủy
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              isLoading={isSubmitting}
              variant="primary"
              size="lg"
            >
              {isSubmitting ? "Đang tạo..." : "Thêm mới"}
            </Button>
          </div>
        </form>
      </div>

      {/* Cancel Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelConfirm}
        title="Xác nhận hủy"
        message="Bạn có chắc chắn muốn hủy thao tác này không?"
      />
    </div>
  );
};

export default EmployeeForm;
